import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWineGlassAlt,
  faSeedling,
  faMapMarkedAlt,
  faBus,
  faCamera,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

const vineyardPhoto = 'https://BelvoirCastle.b-cdn.net/Vineyard/vineyard.webp';

const vineyardBanner = 'https://BelvoirCastle.b-cdn.net/Banners/vineyard.webp';

const ALASDAIR_PHOTO =
  'https://BelvoirCastlePull.b-cdn.net/Website/ALASDAIR%202024.jpg';

const C = {
  navy: '#101d40',
  navySoft: '#1a3352',
  accent: '#9d6d49',
  accentSoft: '#b8875f',
  ivory: '#f8f7f4',
  cream: '#f3f0ea',
  mist: '#e8e4dc',
  ink: '#2a2f3a',
  mute: '#6b7280',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${C.ivory};
  padding-top: 120px;
  color: ${C.ink};
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3rem);
  background: ${p => p.$bg || C.ivory};
  border-bottom: ${p => (p.$border ? `1px solid ${C.mist}` : 'none')};
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeUp} 0.65s ease both;
`;

const Kicker = styled.p`
  margin: 0 0 0.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.accent};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.accent};
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1.25rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: 600;
  line-height: 1.15;
  color: ${C.navy};

  em {
    font-style: italic;
    color: ${C.accent};
  }
`;

const Lead = styled.p`
  margin: 0 0 1.15rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.15rem, 2.2vw, 1.35rem);
  line-height: 1.7;
  color: ${C.navySoft};
  max-width: 38rem;
`;

const Body = styled.p`
  margin: 0 0 1rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.75;
  color: ${C.ink};
  max-width: 40rem;
  opacity: 0.88;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${C.accent};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1.05fr 0.95fr'};
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: ${p => p.$align || 'start'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const PhotoFrame = styled.div`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  min-height: clamp(280px, 42vw, 460px);
  background: ${C.navy};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    min-height: clamp(280px, 42vw, 460px);
  }
`;

/* ── Dates strip ─────────────────────────────────────────── */

const DatesPanel = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 0;
  background: ${C.navy};
  color: #fff;
  overflow: hidden;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const DatesLeft = styled.div`
  padding: clamp(2.5rem, 5vw, 3.5rem);
  border-right: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 800px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const DatesRight = styled.div`
  padding: clamp(2.5rem, 5vw, 3.5rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.25rem;
  background: linear-gradient(160deg, ${C.navy} 0%, #162448 100%);
`;

const DateList = styled.ul`
  list-style: none;
  margin: 1.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const DateRow = styled.li`
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.35rem, 2.5vw, 1.7rem);
  font-weight: 500;
  color: #fff;
  line-height: 1.2;
`;

const ComingSoonNote = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.2rem, 2.2vw, 1.5rem);
  font-weight: 500;
  line-height: 1.4;
  color: #fff;
`;

const SmallPrint = styled.p`
  margin: 1.5rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.01em;
`;

const SmallPrintMuted = styled.p`
  margin: 1.75rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  line-height: 1.55;
  color: ${C.mute};
  max-width: 36rem;
`;

/* ── Itinerary ───────────────────────────────────────────── */

const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  counter-reset: step;
`;

const Step = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.15rem;
  padding: 1.35rem 0;
  border-bottom: 1px solid ${C.mist};
  counter-increment: step;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const StepIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${C.cream};
  color: ${C.accent};
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 0.15rem;
`;

const StepBody = styled.div``;

const StepTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.35rem;
  font-weight: 600;
  color: ${C.navy};
`;

const StepText = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.98rem;
  line-height: 1.65;
  color: ${C.ink};
  opacity: 0.85;
`;

/* ── Guide ───────────────────────────────────────────────── */

const GuidePortrait = styled.div`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: ${C.mist};
  aspect-ratio: 3 / 4;
  max-width: 340px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
  }

  @media (max-width: 860px) {
    max-width: 280px;
    margin: 0 auto;
  }
`;

const GuideName = styled.h3`
  margin: 0 0 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 600;
  color: ${C.navy};
`;

const GuideRole = styled.p`
  margin: 0 0 1.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.accent};
`;

/* ── Plan your visit ─────────────────────────────────────── */

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoItem = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 200px) 1fr;
  gap: 1.5rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid ${C.mist};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`;

const InfoLabel = styled.h3`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${C.navy};
`;

const InfoText = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.98rem;
  line-height: 1.7;
  color: ${C.ink};
  opacity: 0.85;

  a {
    color: ${C.accent};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/* ── What's Included ─────────────────────────────────────── */

const IncludeGrid = styled.ul`
  list-style: none;
  margin: 2rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;

const IncludeItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.15rem 1.25rem;
  background: ${C.ivory};
  border: 1px solid ${C.mist};
  border-radius: 4px;
`;

const IncludeIcon = styled.div`
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${C.cream};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C.accent};
  font-size: 0.85rem;
  margin-top: 0.1rem;
`;

const IncludeText = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${C.ink};
  opacity: 0.88;
`;

/* ── CTA ─────────────────────────────────────────────────── */

const CtaBlock = styled.div`
  text-align: center;
  max-width: 520px;
  margin: 0 auto;
`;

const BookBtn = styled.a`
  display: inline-block;
  background: ${C.accent};
  color: #fff;
  padding: 1rem 2.25rem;
  border-radius: 4px;
  text-decoration: none;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: background 0.2s ease, transform 0.18s ease;
  pointer-events: ${p => (p.$disabled ? 'none' : 'auto')};
  opacity: ${p => (p.$disabled ? 0.7 : 1)};

  &:hover {
    background: ${C.navy};
    transform: ${p => (p.$disabled ? 'none' : 'translateY(-1px)')};
  }
`;

const DurationNote = styled.p`
  margin: 1.5rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  color: ${C.mute};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  svg {
    color: ${C.accent};
  }
`;

const STEPS = [
  {
    icon: faMapMarkedAlt,
    title: 'Begin at the Farm Shop',
    text: 'Our English wine-making tour begins at the Belvoir Castle Retail Village Farm Shop — a celebration of local produce, artisan craft, and estate-grown goodness. Here you\'ll meet your fellow travellers, receive your first taste of the estate, and prepare for an adventure that takes you from castle grounds to working vineyard.',
  },
  {
    icon: faBus,
    title: 'Step Aboard the Gun Bus',
    text: 'Leave the ordinary behind. You\'ll travel to the vineyard aboard our iconic Gun Bus — a vehicle with character as distinctive as the wines themselves. The journey through ancient parkland, sweeping views, and the anticipation of vines waiting to be discovered sets the scene perfectly.',
  },
  {
    icon: faSeedling,
    title: 'Explore the Vines with a Belvoir Estate Expert',
    text: 'Your guides will reveal the story of the vineyard — the grape varieties chosen, the unique microclimate and soils that influence every glass, and the centuries of heritage that shape every bottle. This is not a scripted walk; it\'s a conversation, shaped by your curiosity.',
  },
  {
    icon: faWineGlassAlt,
    title: 'A Tasting Like No Other with Alasdair Lawson-Dick',
    text: 'The journey culminates in a guided tasting of four estate wines — a beautifully presented flight exploring the full character of our range. From crisp, expressive whites to elegant sparkling wines, Alasdair will guide you through each wine as you take in panoramic views across the estate.',
  },
  {
    icon: faCamera,
    title: 'The Return',
    text: 'When you\'re ready, the Gun Bus carries you back to the Farm Shop, where the experience continues. Browse our estate wines, enjoy your exclusive 20% discount, and explore the artisan producers and local makers who share the Belvoir commitment to quality and craft.',
  },
];

const VineyardTour = () => {
  return (
    <Page>
      <ParallaxBanner
        title="Belvoir Castle Vineyard Experience"
        subtitle="Where Heritage Meets the Vine"
        image={vineyardBanner}
      />

      {/* Intro */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCol $align="center">
            <div>
              <Kicker>The Experience</Kicker>
              <Divider />
              <SectionTitle>
                Discover the Belvoir Castle <em>Vineyard Experience</em>
              </SectionTitle>
              <Lead>
                Where Heritage Meets the Vine
              </Lead>
              <Body>
                Nestled within the rolling estate of Belvoir Castle lies a vineyard whose story is
                woven into the very fabric of the land. These slopes have cradled vines since Roman
                times, and the vines were replanted in 2019 — completing a circle of English
                wine-making history at Belvoir.
              </Body>
              <Body>
                Now, we invite you to step into that story — to walk the rows, taste the wines, and
                discover the craft that turns our estate-grown grapes into bottles worthy of the
                Belvoir name. This is more than a tour. It's an immersion into a living tradition,
                set against one of the most breathtaking backdrops in the East Midlands.
              </Body>
              <div style={{ marginTop: '1.75rem' }}>
                <BookBtn as="span" $disabled>More dates coming in 2027</BookBtn>
              </div>
            </div>
            <PhotoFrame>
              <img src={vineyardPhoto} alt="Belvoir Castle Vineyard" />
            </PhotoFrame>
          </TwoCol>
        </Inner>
      </Section>

      {/* Dates */}
      <Section $bg={C.cream} style={{ paddingTop: 0, paddingBottom: 0 }}>
        <DatesPanel>
          <DatesLeft>
            <Kicker style={{ color: C.accentSoft }}>Coming in 2027</Kicker>
            <SectionTitle style={{ color: '#fff', marginBottom: 0 }}>
              Tour <em style={{ color: C.accentSoft }}>dates</em>
            </SectionTitle>
            <DateList>
              <DateRow>More dates coming in 2027</DateRow>
            </DateList>
            <SmallPrint>
              Suitable for guests aged 18 and over. Challenge 25 will be in effect.
            </SmallPrint>
          </DatesLeft>
          <DatesRight>
            <Kicker style={{ color: C.accentSoft, marginBottom: 0 }}>Watch this space</Kicker>
            <ComingSoonNote>
              New vineyard tour dates will be announced here for 2027.
            </ComingSoonNote>
          </DatesRight>
        </DatesPanel>
      </Section>

      {/* Itinerary */}
      <Section $bg="#fff" $border>
        <Inner>
          <TwoCol $cols="0.85fr 1.15fr">
            <div>
              <Kicker>Your Journey</Kicker>
              <Divider />
              <SectionTitle>
                Your journey through <em>the vineyard</em>
              </SectionTitle>
              <Body>
                From the Farm Shop to the vines and back — an immersive experience shaped by expert
                knowledge, estate history, and the wines themselves.
              </Body>
              <DurationNote as="div" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                <FontAwesomeIcon icon={faClock} />
                Approximately 2.5 hours
              </DurationNote>
            </div>
            <Steps>
              {STEPS.map(step => (
                <Step key={step.title}>
                  <StepIcon>
                    <FontAwesomeIcon icon={step.icon} />
                  </StepIcon>
                  <StepBody>
                    <StepTitle>{step.title}</StepTitle>
                    <StepText>{step.text}</StepText>
                  </StepBody>
                </Step>
              ))}
            </Steps>
          </TwoCol>
        </Inner>
      </Section>

      {/* What's Included */}
      <Section $bg={C.cream} $border>
        <Inner>
          <Kicker>What's Included</Kicker>
          <Divider />
          <SectionTitle>
            Everything in your <em>experience</em>
          </SectionTitle>
          <IncludeGrid>
            <IncludeItem>
              <IncludeIcon><FontAwesomeIcon icon={faMapMarkedAlt} /></IncludeIcon>
              <IncludeText>Welcome taste at the Belvoir Castle Retail Village Farm Shop</IncludeText>
            </IncludeItem>
            <IncludeItem>
              <IncludeIcon><FontAwesomeIcon icon={faBus} /></IncludeIcon>
              <IncludeText>Gun Bus transport to and from the vineyard</IncludeText>
            </IncludeItem>
            <IncludeItem>
              <IncludeIcon><FontAwesomeIcon icon={faSeedling} /></IncludeIcon>
              <IncludeText>Guided vineyard walk with Belvoir estate expert</IncludeText>
            </IncludeItem>
            <IncludeItem>
              <IncludeIcon><FontAwesomeIcon icon={faWineGlassAlt} /></IncludeIcon>
              <IncludeText>Guided tasting of four estate wines with Alasdair Lawson-Dick</IncludeText>
            </IncludeItem>
            <IncludeItem>
              <IncludeIcon><FontAwesomeIcon icon={faCamera} /></IncludeIcon>
              <IncludeText>Time at leisure to enjoy the views and capture photographs</IncludeText>
            </IncludeItem>
          </IncludeGrid>
        </Inner>
      </Section>

      {/* Guide */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCol $cols="0.75fr 1.25fr" $align="start">
            <GuidePortrait>
              <img
                src={ALASDAIR_PHOTO}
                alt="Alasdair Lawson-Dick, vineyard tour guide at Belvoir Castle"
              />
            </GuidePortrait>
            <div>
              <Kicker>Your Guide</Kicker>
              <Divider />
              <GuideName>Alasdair Lawson-Dick</GuideName>
              <GuideRole>Wine specialist &amp; tour guide</GuideRole>
              <Body>
                It was a school Wine Society guest speaker — a local wine merchant — who first
                encouraged Alasdair to pursue a career in the Wine Trade. And thereafter, tactfully
                arranged for him to spend an eventful year working at Bordeaux property Château La
                Bourgette, assisting owner Mon Bertrand Lalande with everything from loading export
                orders onto lorries to endless hours in the vineyards pruning and harvesting. In
                1986 Alasdair then found himself back in leafy Primrose Hill at newly launched
                Bibendum Wine. This is where he started to really learn about, and understand, great
                wine.
              </Body>
              <Body>
                Some years later, with various WSET Wine Exam passes under his belt, Alasdair moved
                to the Arabian Gulf to work in the Dubai wine trade, supplying the fast-growing
                hospitality sector and expat community. Twelve years later he returned to start
                working for Fine Wine importer Justerini &amp; Brooks based in Edinburgh. Now,
                happily returning to Rutland, he is thrilled to have joined the specialist wine
                importer Private Cellar.
              </Body>
              <Body>
                He is a big fan of English wines. His favourite non-English wines are red Bordeaux,
                red and white Burgundy, Barolo, and old Vintage Port. He looks forward to sharing
                what makes Belvoir Wines special with you on the tour.
              </Body>
            </div>
          </TwoCol>
        </Inner>
      </Section>

      {/* Plan your visit */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>Practical Information</Kicker>
          <Divider />
          <SectionTitle>
            Plan your <em>visit</em>
          </SectionTitle>
          <InfoList>
            <InfoItem>
              <InfoLabel>Departure point</InfoLabel>
              <InfoText>
                Belvoir Castle Retail Village Farm Shop — where the tour starts and ends.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Duration</InfoLabel>
              <InfoText>
                Approximately 2.5 hours.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Group size</InfoLabel>
              <InfoText>
                Limited to 15 people to ensure an intimate experience — early booking is recommended
                to avoid disappointment.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Accessibility</InfoLabel>
              <InfoText>
                The vineyard tour includes exploring the vineyard which has uneven ground and
                vineyard rows. If you have limited mobility please consider you may not feel
                comfortable with this element of the tour. If you are a wheelchair user please
                consider there is unfortunately no step free access aboard our tour bus and we
                recommend being accompanied by a strong companion. Please contact us if you have
                specific accessibility requirements and we will do our best to accomodate.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>What to wear</InfoLabel>
              <InfoText>
                Comfortable outdoor clothing and footwear suitable for walking on uneven ground and
                vineyard rows. Layers are recommended for changeable English weather. We also
                recommend sun protection as there is limited shade at the Vineyard.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Dietary</InfoLabel>
              <InfoText>
                Please advise your dietary requirements when booking so we can prepare accordingly.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Parking</InfoLabel>
              <InfoText>
                Details are on our{' '}
                <a
                  href="https://www.belvoircastle.com/about/parking"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  parking page
                </a>
                . Accessible parking is limited but available — please speak to Vale View Cafe on
                arrival.
              </InfoText>
            </InfoItem>
          </InfoList>
        </Inner>
      </Section>

      {/* CTA */}
      <Section $bg={C.cream}>
        <Inner>
          <CtaBlock>
            <Kicker>Book Your Vineyard Experience</Kicker>
            <Divider style={{ margin: '0 auto 1.25rem' }} />
            <SectionTitle style={{ textAlign: 'center' }}>
              Discover the wine. Discover the <em>place</em>.
            </SectionTitle>
            <Body style={{ margin: '0 auto 1.75rem', textAlign: 'center' }}>
              The vineyard is a living, working estate — and every season brings its own character.
              Whenever you visit, you'll find the vines telling their own chapter of the Belvoir
              story. More dates coming in 2027.
            </Body>
            <BookBtn as="span" $disabled>
              More dates coming in 2027
            </BookBtn>
            <SmallPrintMuted style={{ margin: '1.25rem auto 0', textAlign: 'center' }}>
              Suitable for guests aged 18 and over. Challenge 25 will be in effect.
            </SmallPrintMuted>
          </CtaBlock>
        </Inner>
      </Section>

      <Footer />
    </Page>
  );
};

export default VineyardTour;
